import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronRightIcon, CodeBracketIcon } from "@heroicons/react/24/outline";
import { buildAiRoutingPayload, defaultAiRoutingForm, normalizeAiRouting } from "./aiInboxRoutingUtils";

const PROVIDERS = [
  { value: "chatgpt", labelKey: "microsoft.ai_mailboxes.ai_routing.provider_chatgpt" },
  { value: "grok", labelKey: "microsoft.ai_mailboxes.ai_routing.provider_grok" },
];

function CategoryCard({ title, description, children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      {description ? <p className="mt-0.5 text-xs text-gray-600">{description}</p> : null}
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled }) {
  const { t } = useTranslation();
  return (
    <div>
      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</label>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {t(o.labelKey)}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, type = "text", disabled, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
      />
      {hint ? <p className="mt-0.5 text-[10px] text-gray-500">{hint}</p> : null}
    </div>
  );
}

/**
 * Brukervennlig redigering av ai_inbox_routing — lagres som JSON via buildAiRoutingPayload.
 */
export function AiInboxRoutingForm({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const form = useMemo(() => normalizeAiRouting(value), [value]);

  const patch = (partial) => {
    onChange({ ...defaultAiRoutingForm(), ...form, ...partial });
  };

  const patchBucket = (key, bucketPatch) => {
    const cur = form[key] && typeof form[key] === "object" ? form[key] : {};
    patch({ [key]: { ...cur, ...bucketPatch } });
  };

  const promoActions = [
    { value: "none", labelKey: "microsoft.ai_mailboxes.ai_routing.action_none" },
    { value: "forward", labelKey: "microsoft.ai_mailboxes.ai_routing.action_forward" },
    { value: "move_archive", labelKey: "microsoft.ai_mailboxes.ai_routing.action_move_archive" },
    { value: "move_junk", labelKey: "microsoft.ai_mailboxes.ai_routing.action_move_junk" },
    { value: "delete_graph", labelKey: "microsoft.ai_mailboxes.ai_routing.action_delete_graph" },
  ];

  const orderActions = [
    { value: "none", labelKey: "microsoft.ai_mailboxes.ai_routing.action_none" },
    { value: "forward", labelKey: "microsoft.ai_mailboxes.ai_routing.action_forward" },
    { value: "move_folder", labelKey: "microsoft.ai_mailboxes.ai_routing.action_move_folder" },
  ];

  const inquiryActions = [
    { value: "none", labelKey: "microsoft.ai_mailboxes.ai_routing.action_none" },
    { value: "create_case", labelKey: "microsoft.ai_mailboxes.ai_routing.action_create_case" },
    {
      value: "create_case_and_reply",
      labelKey: "microsoft.ai_mailboxes.ai_routing.action_create_case_and_reply",
    },
  ];

  const supplierActions = [
    { value: "none", labelKey: "microsoft.ai_mailboxes.ai_routing.action_none" },
    { value: "append_case_note", labelKey: "microsoft.ai_mailboxes.ai_routing.action_append_case_note" },
  ];

  const caseReplyActions = [
    { value: "none", labelKey: "microsoft.ai_mailboxes.ai_routing.action_none" },
    { value: "customer_communication", labelKey: "microsoft.ai_mailboxes.ai_routing.action_customer_communication" },
  ];

  const projectCorrespondenceActions = [
    { value: "none", labelKey: "microsoft.ai_mailboxes.ai_routing.action_none" },
    { value: "reply_draft_only", labelKey: "microsoft.ai_mailboxes.ai_routing.action_reply_draft_only" },
  ];

  const pa = form.promotional?.action || "none";
  const oa = form.order_confirmation?.action || "none";
  const sa = form.supplier_cas?.action || "none";

  const [jsonOpen, setJsonOpen] = useState(false);
  const previewJson = useMemo(() => JSON.stringify(buildAiRoutingPayload(form), null, 2), [form]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border border-indigo-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-900">
          <input
            type="checkbox"
            checked={Boolean(form.enabled)}
            disabled={disabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          {t("microsoft.ai_mailboxes.ai_routing.master_enable")}
        </label>
        <p className="text-xs text-gray-600 sm:max-w-md">{t("microsoft.ai_mailboxes.ai_routing.master_hint")}</p>
      </div>

      <CategoryCard
        title={t("microsoft.ai_mailboxes.ai_routing.reply_draft_title")}
        description={t("microsoft.ai_mailboxes.ai_routing.reply_draft_desc")}
      >
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-900">
          <input
            type="checkbox"
            checked={Boolean(form.reply_draft?.enabled)}
            disabled={disabled || !form.enabled}
            onChange={(e) => patchBucket("reply_draft", { enabled: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
          />
          {t("microsoft.ai_mailboxes.ai_routing.reply_draft_enable")}
        </label>
        {!form.enabled ? (
          <p className="text-xs text-amber-800">{t("microsoft.ai_mailboxes.ai_routing.reply_draft_requires_ai")}</p>
        ) : null}
      </CategoryCard>

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label={t("microsoft.ai_mailboxes.ai_routing.provider_label")}
          value={form.provider}
          disabled={disabled}
          onChange={(v) => patch({ provider: v })}
          options={PROVIDERS}
        />
        <TextField
          label={t("microsoft.ai_mailboxes.ai_routing.model_label")}
          value={form.model || ""}
          disabled={disabled}
          onChange={(v) => patch({ model: v })}
          placeholder={t("microsoft.ai_mailboxes.ai_routing.model_placeholder")}
          hint={t("microsoft.ai_mailboxes.ai_routing.model_hint")}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <CategoryCard
          title={t("microsoft.ai_mailboxes.ai_routing.cat_promotional_title")}
          description={t("microsoft.ai_mailboxes.ai_routing.cat_promotional_desc")}
        >
          <SelectField
            label={t("microsoft.ai_mailboxes.ai_routing.action_label")}
            value={pa}
            disabled={disabled}
            onChange={(v) => patchBucket("promotional", { action: v })}
            options={promoActions}
          />
          {pa === "forward" ? (
            <TextField
              label={t("microsoft.ai_mailboxes.ai_routing.forward_to_label")}
              type="email"
              value={form.promotional?.forward_to || ""}
              disabled={disabled}
              onChange={(v) => patchBucket("promotional", { forward_to: v })}
              placeholder="okonomi@firma.no"
            />
          ) : null}
          {pa === "move_archive" || pa === "move_junk" ? (
            <TextField
              label={t("microsoft.ai_mailboxes.ai_routing.folder_optional_label")}
              value={form.promotional?.folder_name || ""}
              disabled={disabled}
              onChange={(v) => patchBucket("promotional", { folder_name: v })}
              placeholder={
                pa === "move_junk"
                  ? t("microsoft.ai_mailboxes.ai_routing.folder_placeholder_junk")
                  : t("microsoft.ai_mailboxes.ai_routing.folder_placeholder_archive")
              }
              hint={t("microsoft.ai_mailboxes.ai_routing.folder_optional_hint")}
            />
          ) : null}
        </CategoryCard>

        <CategoryCard
          title={t("microsoft.ai_mailboxes.ai_routing.cat_order_title")}
          description={t("microsoft.ai_mailboxes.ai_routing.cat_order_desc")}
        >
          <SelectField
            label={t("microsoft.ai_mailboxes.ai_routing.action_label")}
            value={oa}
            disabled={disabled}
            onChange={(v) => patchBucket("order_confirmation", { action: v })}
            options={orderActions}
          />
          {oa === "forward" ? (
            <TextField
              label={t("microsoft.ai_mailboxes.ai_routing.forward_to_label")}
              type="email"
              value={form.order_confirmation?.forward_to || ""}
              disabled={disabled}
              onChange={(v) => patchBucket("order_confirmation", { forward_to: v })}
              placeholder="ordre@firma.no"
            />
          ) : null}
          {oa === "move_folder" ? (
            <TextField
              label={t("microsoft.ai_mailboxes.ai_routing.folder_name_label")}
              value={form.order_confirmation?.folder_name || ""}
              disabled={disabled}
              onChange={(v) => patchBucket("order_confirmation", { folder_name: v })}
              placeholder={t("microsoft.ai_mailboxes.ai_routing.folder_placeholder_order")}
              hint={t("microsoft.ai_mailboxes.ai_routing.folder_order_hint")}
            />
          ) : null}
        </CategoryCard>

        <CategoryCard
          title={t("microsoft.ai_mailboxes.ai_routing.cat_inquiry_title")}
          description={t("microsoft.ai_mailboxes.ai_routing.cat_inquiry_desc")}
        >
          <SelectField
            label={t("microsoft.ai_mailboxes.ai_routing.action_label")}
            value={form.customer_inquiry?.action || "none"}
            disabled={disabled}
            onChange={(v) => patchBucket("customer_inquiry", { action: v })}
            options={inquiryActions}
          />
          <p className="text-[10px] text-gray-500">{t("microsoft.ai_mailboxes.ai_routing.cat_inquiry_auto_reply_note")}</p>
          {(form.customer_inquiry?.action || "none") === "create_case_and_reply" ? (
            <p className="rounded-md border border-amber-100 bg-amber-50/80 px-2 py-1.5 text-[10px] text-amber-950">
              {t("microsoft.ai_mailboxes.ai_routing.create_case_and_reply_hint")}
            </p>
          ) : null}
        </CategoryCard>

        <CategoryCard
          title={t("microsoft.ai_mailboxes.ai_routing.cat_supplier_cas_title")}
          description={t("microsoft.ai_mailboxes.ai_routing.cat_supplier_cas_desc")}
        >
          <SelectField
            label={t("microsoft.ai_mailboxes.ai_routing.action_label")}
            value={sa}
            disabled={disabled}
            onChange={(v) => patchBucket("supplier_cas", { action: v })}
            options={supplierActions}
          />
          {sa === "append_case_note" ? (
            <>
              <TextField
                label={t("microsoft.ai_mailboxes.ai_routing.forward_unmatched_label")}
                type="email"
                value={form.supplier_cas?.forward_unmatched_to || ""}
                disabled={disabled}
                onChange={(v) => patchBucket("supplier_cas", { forward_unmatched_to: v })}
                placeholder="saksbehandler@firma.no"
                hint={t("microsoft.ai_mailboxes.ai_routing.forward_unmatched_hint")}
              />
              <TextField
                label={t("microsoft.ai_mailboxes.ai_routing.cas_reference_label")}
                value={form.supplier_cas?.cas_reference || ""}
                disabled={disabled}
                onChange={(v) => patchBucket("supplier_cas", { cas_reference: v })}
                placeholder="CAS-123456-ABCDE"
                hint={t("microsoft.ai_mailboxes.ai_routing.cas_reference_hint")}
              />
              <TextField
                label={t("microsoft.ai_mailboxes.ai_routing.case_number_fallback_label")}
                value={form.supplier_cas?.case_number || ""}
                disabled={disabled}
                onChange={(v) => patchBucket("supplier_cas", { case_number: v })}
                placeholder="1234"
                hint={t("microsoft.ai_mailboxes.ai_routing.case_number_fallback_hint")}
              />
            </>
          ) : null}
        </CategoryCard>

        <CategoryCard
          title={t("microsoft.ai_mailboxes.ai_routing.cat_case_reply_title")}
          description={t("microsoft.ai_mailboxes.ai_routing.cat_case_reply_desc")}
        >
          <SelectField
            label={t("microsoft.ai_mailboxes.ai_routing.action_label")}
            value={form.case_reply?.action || "none"}
            disabled={disabled}
            onChange={(v) => patchBucket("case_reply", { action: v })}
            options={caseReplyActions}
          />
        </CategoryCard>

        <CategoryCard
          title={t("microsoft.ai_mailboxes.ai_routing.cat_project_title")}
          description={t("microsoft.ai_mailboxes.ai_routing.cat_project_desc")}
        >
          <SelectField
            label={t("microsoft.ai_mailboxes.ai_routing.action_label")}
            value={form.project_correspondence?.action || "none"}
            disabled={disabled}
            onChange={(v) => patchBucket("project_correspondence", { action: v })}
            options={projectCorrespondenceActions}
          />
          <p className="text-[10px] text-gray-500">{t("microsoft.ai_mailboxes.ai_routing.cat_project_reply_draft_note")}</p>
        </CategoryCard>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setJsonOpen((o) => !o)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-100"
        >
          <CodeBracketIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          <span className="flex-1">{t("microsoft.ai_mailboxes.ai_routing.json_preview_toggle")}</span>
          {jsonOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {jsonOpen ? (
          <pre className="max-h-48 overflow-auto border-t border-gray-200 bg-gray-900/95 p-3 font-mono text-[10px] leading-relaxed text-emerald-100">
            {previewJson}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
